import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

interface SnoozeClearTriageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: { timePeriod?: string; reason?: string; action: "snooze" | "clear" | "false_alarm" }) => void;
  isLoading?: boolean;
}

const SnoozeClearTriageDialog: React.FC<SnoozeClearTriageDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<string>("");
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [actionType, setActionType] = useState<"snooze" | "clear" | "false_alarm">("snooze");

  const timePeriods = ["15 minutes", "30 minutes", "1 hour"];

  const clearTriageReasons = [
    "Will Continue To Monitor and Trend",
    "Improper use of Equipment",
    "Transmission Not Concerning At This Time",
    "Trends Are Not Concerning At This Time",
    "Reviewed with Provider",
    "Reviewed with RN",
    "No Further Actions Required",
    "Invalid Reading",
  ];

  const falseAlarmReasons = [
    "Equipment Malfunction",
    "Incorrect Reading",
    "Test Reading",
    "Patient Not Present",
    "Other",
  ];

  const handleTimePeriodClick = (period: string) => {
    setSelectedTimePeriod(period);
    setActionType("snooze");
    setSelectedReason(""); // Clear reason when selecting time period
  };

  const handleClearTriageClick = () => {
    setActionType("clear");
    setSelectedTimePeriod(""); // Clear time period when selecting clear triage
    setSelectedReason(""); // Clear reason when switching
  };

  const handleFalseAlarmClick = () => {
    setActionType("false_alarm");
    setSelectedTimePeriod(""); // Clear time period when selecting false alarm
    setSelectedReason(""); // Clear reason when switching
  };

  const handleConfirm = () => {
    if (actionType === "snooze" && !selectedTimePeriod) {
      return; // Don't allow confirmation without time period
    }
    if ((actionType === "clear" || actionType === "false_alarm") && !selectedReason) {
      return; // Don't allow confirmation without reason
    }

    onConfirm({
      timePeriod: selectedTimePeriod || undefined,
      reason: selectedReason || undefined,
      action: actionType,
    });
  };

  const handleClose = () => {
    setSelectedTimePeriod("");
    setSelectedReason("");
    setActionType("snooze");
    onClose();
  };

  const isConfirmDisabled = 
    (actionType === "snooze" && !selectedTimePeriod) ||
    ((actionType === "clear" || actionType === "false_alarm") && !selectedReason);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Snooze/Clear Triage.
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Select Time Period Section */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Select Time Period</Label>
            <div className="flex gap-2 flex-wrap">
              {timePeriods.map((period) => (
                <Button
                  key={period}
                  type="button"
                  variant={selectedTimePeriod === period && actionType === "snooze" ? "default" : "outline"}
                  onClick={() => handleTimePeriodClick(period)}
                  className="flex-1 min-w-[100px]"
                >
                  {period}
                </Button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={actionType === "clear" ? "default" : "outline"}
                onClick={handleClearTriageClick}
                className="flex-1"
              >
                Clear Triage
              </Button>
              <Button
                type="button"
                variant={actionType === "false_alarm" ? "default" : "outline"}
                onClick={handleFalseAlarmClick}
                className="flex-1"
              >
                False Alarm
              </Button>
            </div>
          </div>

          {/* Select Reason Section - Show when Clear Triage is selected */}
          {actionType === "clear" && (
            <div className="space-y-2">
              <Label htmlFor="reason-select" className="text-base font-semibold">
                Select reason for clear triage <span className="text-red-500">*</span>
              </Label>
              <Select
                value={selectedReason}
                onValueChange={setSelectedReason}
              >
                <SelectTrigger id="reason-select" className="w-full">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {clearTriageReasons.map((reason) => (
                    <SelectItem key={reason} value={reason}>
                      {reason}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Input Box Section - Show when False Alarm is selected */}
          {actionType === "false_alarm" && (
            <div className="space-y-2">
              <Label htmlFor="false-alarm-reason" className="text-base font-semibold">
                Dismissal Reason <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="false-alarm-reason"
                placeholder="Enter reason for dismissing this alert"
                value={selectedReason}
                onChange={(e) => setSelectedReason(e.target.value)}
                rows={4}
                className="w-full"
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="border-red-500 text-red-500 hover:bg-red-50"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={isConfirmDisabled || isLoading}
              variant="default"
            >
              {isLoading ? "Processing..." : "Confirm"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SnoozeClearTriageDialog;

